import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { useEffect, useState } from 'react';
import { getSession, User } from './services/api';
import { useAuthInterceptor } from './hooks/useAuthInterceptor';
import { UserProvider } from './contexts/UserContext';
import { LoginPage } from './pages/LoginPage';
import { MagicLoginRedirect } from './pages/MagicLoginRedirect';
import { Event_Overview_Page} from './pages/Event/EventOverviewPage';
import EventParticipants from './pages/Event/Participants/EventParticipantsPage';
import EventVotePage from './pages/Event/EventVote/EventVotePage';
import EventRounds from './pages/Event/EventRounds/EventRounds';
import EventRegister from './pages/Event/EventRegister';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsersDashboard from './pages/User/AdminUsersDashboard';
import AdminUserDetail from './pages/User/AdminUserDetail';
import Admin_User_Create from './pages/User/AdminUserCreate';
import AdminUserRoles from './pages/User/AdminUserRoles';
import UserProfile from './pages/User/UserProfile';
import UserVerify from './pages/User/UserVerify';
import AdminFirmDashboard from './pages/Firm/AdminFirmDashboard';
import AdminFirmDetail from './pages/Firm/AdminFirmDetail2';
import AdminFirmCreate from './pages/Firm/AdminFirmCreate';
import LocationManagement from './pages/Location/LocationManagement';
import LocationEdit from './pages/Location/LocationEdit';
import AdminErrorDashboard from './pages/Error/AdminErrorDashboard';
import JobManagement from './pages/Jobs/JobManagement';
import JobLogsOverview from './pages/Jobs/JobLogsOverview';
import JobLogsDetail from './pages/Jobs/JobLogsDetail';
import RoleManagement from './pages/Role/RoleManagement';
import ApiKeyManagement from './pages/API/ApiKeyManagement';
import { PrivateRoute } from './components/PrivateRoute';
import { NavBar } from './components/NavBar';
import ProfileModal from './components/ProfileModal';
import CookieBanner from './components/Cookie-Banner';
import { DevelopmentBanner } from './components/DevelopmentBanner';
import { IS_DEV_ENVIRONMENT } from './config/environment';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; // für Toggler-Menü


import './App.css';
// Import der CSS-Datei, die alle Hover-Effekte deaktiviert (als letztes importieren, damit sie Vorrang hat)
import EventSpotlight from './pages/Event/EventSpotlight/EventSpotlight';
import EventView from './pages/Event/EventView/EventView';
import LunchRouletteOverviewPage from './pages/LunchRoulette/LunchRouletteOverviewPage';
import LunchRouletteAdminPanel from './pages/LunchRoulette/LunchRouletteAdminPanel';
import LunchRouletteMyMatches from './pages/LunchRoulette/LunchRouletteMyMatches';


function App() {

  
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    setUser(null);
    setRoles([]);
    // Note: profileModalDismissedAt bleibt in localStorage erhalten (7-Tage-Regel)
  };

  useEffect(() => {
    getSession().then((res) => {
      if (res && res.authenticated && res.user) {
        setUser(res.user);
        // Store roles from the response
        if (res.roles && Array.isArray(res.roles)) {
          setRoles(res.roles);
        }
      }
      setLoading(false);
    }).catch((err) => {
      console.error("Fehler beim Abrufen der Session:", err);
      // Bei Fehlern auch den Benutzer abmelden
      handleLogout();
      setLoading(false);
    });
  }, []);

  // Listen for auth-session-expired events from apiClient
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔴 [App] Received auth-session-expired event:', customEvent.detail);
      
      // Clear user state and redirect will be handled by router
      handleLogout();
    };

    window.addEventListener('auth-session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth-session-expired', handleSessionExpired);
    };
  }, []);

  // Add dev-mode class to body when in development environment
  useEffect(() => {
    if (IS_DEV_ENVIRONMENT) {
      document.body.classList.add('dev-mode');
    } else {
      document.body.classList.remove('dev-mode');
    }

    return () => {
      document.body.classList.remove('dev-mode');
    };
  }, []);

  // App-Wrapper-Komponente für Auth-Interceptor
  const AppContent = () => {
    useAuthInterceptor(handleLogout);
    
    if (loading) return <div>Lade...</div>;

    return (
      <>
        {/* Development Banner - only shown in dev environment */}
        {IS_DEV_ENVIRONMENT && <DevelopmentBanner />}
        
        <UserProvider roles={roles} user={user}>
          <NavBar user={user} roles={roles} onLogout={handleLogout} />
          {/* Profile Modal für unvollständige Profile */}
          <ProfileModal user={user} />
          {/* Toast Container für globale Benachrichtigungen */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          {/* Cookie Banner - global für alle Seiten */}
          <CookieBanner />
          <Routes>
          <Route path="/" element={<Navigate to="/events" />} />
          <Route path="/login" element={<LoginPage onLogin={setUser} />} />
          <Route path="/magic-login" element={<MagicLoginRedirect onLogin={setUser} />} />
          <Route path="/verify" element={<UserVerify />} />
          <Route path="/register" element={<EventRegister />} />
          <Route path="/spotlight" element={<EventSpotlight />} />
          <Route
            path="/events"
            element={
              <PrivateRoute user={user} roles={roles}>
                <Event_Overview_Page />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:eventId/view"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Event Admin", "Reader"]} roles={roles}>
                <EventView />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:eventId/participants"
            element={
              <PrivateRoute user={user} roles={roles}>
                <EventParticipants />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:eventId/vote"
            element={
              <PrivateRoute user={user} roles={roles}>
                <EventVotePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/events/:eventId/rounds"
            element={
              <PrivateRoute user={user} roles={roles}>
                <EventRounds />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin"]} roles={roles}>
                <AdminDashboard user={user} roles={roles} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "User Admin", "User Role Admin"]} roles={roles} pageContext="users">
                <AdminUsersDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users/create"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "User Admin"]} roles={roles} pageContext="users">
                <Admin_User_Create />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "User Admin", "User Role Admin"]} roles={roles} pageContext="users">
                <AdminUserDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users/:userId/roles"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "User Role Admin"]} roles={roles} pageContext="users">
                <AdminUserRoles />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute user={user} roles={roles}>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/firms"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Firm Admin"]} roles={roles} pageContext="firms">
                <AdminFirmDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/firms/create"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Firm Admin"]} roles={roles} pageContext="firms">
                <AdminFirmCreate />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/firms/:firmId"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Firm Admin"]} roles={roles} pageContext="firms">
                <AdminFirmDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Location Admin"]} roles={roles} pageContext="locations">
                <LocationManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/locations/create"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Location Admin"]} roles={roles} pageContext="locations">
                <LocationEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/locations/:countryId"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Location Admin"]} roles={roles} pageContext="locations">
                <LocationEdit />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Role Admin"]} roles={roles} pageContext="roles">
                <RoleManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "API Admin"]} roles={roles} pageContext="api-keys">
                <ApiKeyManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/errors"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Error Admin"]} roles={roles} pageContext="errors">
                <AdminErrorDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Job Admin", "Log Admin"]} roles={roles} pageContext="jobs">
                <JobManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/job-logs"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Log Admin"]} roles={roles} pageContext="job-logs">
                <JobLogsOverview />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/job-logs/detail"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Log Admin"]} roles={roles} pageContext="job-logs">
                <JobLogsDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/lunch-roulette"
            element={
              <PrivateRoute user={user} roles={roles}>
                <LunchRouletteOverviewPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/lunch-roulette/admin/:rouletteId"
            element={
              <PrivateRoute user={user} requiredRoles={["Admin", "Lunch-Roulette Admin"]} roles={roles}>
                <LunchRouletteAdminPanel />
              </PrivateRoute>
            }
          />
          <Route
            path="/lunch-roulette/my-matches/:rouletteId"
            element={
              <PrivateRoute user={user} roles={roles}>
                <LunchRouletteMyMatches />
              </PrivateRoute>
            }
          />
          <Route path="/*" element={<Navigate to="/events" />} />
        </Routes>
        </UserProvider>
      </>
    );
  };

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
